import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { isRecruiterChannelEnabled } from '@kilat/shared';

import { AuthProvider, useAuth } from './lib/use-auth.js';
import { PlatformBrandProvider, usePlatformBrand } from './lib/use-platform-brand.js';
import { Skeleton, ThemeProvider } from '@kilat/ui';

import { I18nProvider, useI18n } from './lib/use-i18n.js';
import { AppShell } from './components/app-shell.js';
import { SignInScreen } from './components/sign-in-screen.js';
import { PwaUpdateBanner } from './components/pwa-update-banner.js';
import { HomePage } from './routes/home.js';
import { AcademyPage } from './routes/academy.js';
import { EarningsPage } from './routes/earnings.js';
import { PayoutsPage } from './routes/payouts.js';
import { OperatorsPage } from './routes/operators.js';
import { MapPage } from './routes/map.js';
import { CodePage } from './routes/code.js';
import { RecruiterHomePage } from './routes/recruiter-home.js';
import { RecruiterAgentsPage } from './routes/recruiter-agents.js';
import { RecruiterEarningsPage } from './routes/recruiter-earnings.js';
import { RecruiterPayoutsPage } from './routes/recruiter-payouts.js';
import { RecruiterCodePage } from './routes/recruiter-code.js';
import { ProfilePage } from './routes/profile.js';
import { InboxPage } from './routes/inbox.js';
import { AgentPushProvider, AgentPushToast } from './lib/push/agent-push.js';

/**
 * Agent PWA shell.
 *
 * Provider order: Auth → Router → Shell. Sprint 38 ships two real
 * routes (`/` and `/earnings`); the rest of the operators/payouts/code
 * surfaces land in subsequent sprints.
 */
export function App() {
  return (
    <PlatformBrandProvider fallback={<Splash />}>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              {/* Push: inside the router so a tapped notification routes in-app. */}
              <AgentPushProvider>
                <AppShell>
                  <AuthGate>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/home" element={<Navigate to="/" replace />} />
                      <Route path="/academy" element={<AcademyPage />} />
                      <Route path="/earnings" element={<EarningsPage />} />
                      <Route path="/payouts" element={<PayoutsPage />} />
                      <Route path="/operators" element={<OperatorsPage />} />
                      <Route path="/map" element={<MapPage />} />
                      <Route path="/code" element={<CodePage />} />
                      <Route
                        path="/recruiter"
                        element={
                          <RecruiterGate>
                            <RecruiterHomePage />
                          </RecruiterGate>
                        }
                      />
                      <Route
                        path="/recruiter/agents"
                        element={
                          <RecruiterGate>
                            <RecruiterAgentsPage />
                          </RecruiterGate>
                        }
                      />
                      <Route
                        path="/recruiter/earnings"
                        element={
                          <RecruiterGate>
                            <RecruiterEarningsPage />
                          </RecruiterGate>
                        }
                      />
                      <Route
                        path="/recruiter/payouts"
                        element={
                          <RecruiterGate>
                            <RecruiterPayoutsPage />
                          </RecruiterGate>
                        }
                      />
                      <Route
                        path="/recruiter/code"
                        element={
                          <RecruiterGate>
                            <RecruiterCodePage />
                          </RecruiterGate>
                        }
                      />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/inbox" element={<InboxPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </AuthGate>
                  <PwaUpdateBanner />
                  <AgentPushToast />
                </AppShell>
              </AgentPushProvider>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </PlatformBrandProvider>
  );
}

/**
 * Auth gate for the whole app. Replaces the old per-route dead-ends where a
 * signed-out agent (session expired after the one-time `#signInToken` landing)
 * had no way back in:
 *   - while auth is resolving (including consuming a `#signInToken`) → a short
 *     "signing you in" splash, so the login form never flashes over the handoff;
 *   - signed out → the returning-agent `SignInScreen` (Google + email + reset);
 *   - signed in → the routed app (each route still handles its own
 *     no-agent-claim / recruiter state — see routes/home.tsx).
 *
 * Centralising here means a deep link to any route while signed out lands on the
 * sign-in screen rather than a broken authenticated surface.
 */
function AuthGate({ children }: { readonly children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthSplash />;
  if (!user) return <SignInScreen />;
  return <>{children}</>;
}

function AuthSplash() {
  const t = useI18n();
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8) var(--space-4)',
      }}
    >
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {t.t('agent.login.signingIn')}
      </p>
    </main>
  );
}

/**
 * ADR-0023 route guard for the recruiter sub-app. When the platform brand has
 * `capabilities.recruiterChannel === false`, every `/recruiter/*` route
 * redirects to the agent home instead of rendering. Default (flag absent or
 * true) renders the children unchanged, so this is dormant until an owner
 * disables the channel in the panel. The nav affordances are hidden in
 * `app-shell.tsx`; this guard covers direct navigation / bookmarks.
 */
function RecruiterGate({ children }: { readonly children: ReactNode }) {
  const brand = usePlatformBrand();
  if (!isRecruiterChannelEnabled(brand)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function Splash() {
  // The brand-resolution splash renders as the PlatformBrandProvider fallback,
  // BEFORE I18nProvider mounts, so no string here can be localised. It used to
  // say "Loading…" in English to a Malay-speaking agent on a slow phone — now
  // it says nothing, like the dashboard's: a mark and a moving bar.
  return (
    <main className="min-h-dvh flex items-center justify-center p-8">
      <div
        role="status"
        aria-busy="true"
        style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}
      >
        <span
          aria-hidden
          style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-primary)',
            boxShadow: 'var(--shadow-2)',
          }}
        />
        <Skeleton width={140} height={12} radius={999} />
      </div>
    </main>
  );
}
